import React from 'react';
import { ExternalLink, ArrowRight, Leaf, Mountain, Shield, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import { ocsasPlatform } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Featured portfolio section for Smartlab subsidiary — showcases Ocsas Pharma.
 * EU-GMP medical cannabis built on a 49-year concession in Rwanda's Virunga foothills.
 * Branding: Volcanic Green + gold accents.
 */
const OCSAS_GREEN = '#1f3a2a';
const OCSAS_GREEN_DEEP = '#0f1f17';
const OCSAS_GOLD = '#C9A961';

const chipIcons = [Shield, Mountain, Globe, Leaf];

const OcsasSection = () => {
  const { language, pick } = useLanguage();

  return (
    <section
      className="section-padding-lg border-t relative overflow-hidden"
      style={{
        background: `linear-gradient(180deg, ${OCSAS_GREEN_DEEP} 0%, ${OCSAS_GREEN} 100%)`,
        borderColor: `${OCSAS_GOLD}33`,
      }}
      data-testid="ocsas-section"
    >
      {/* Ambient gold & green glow */}
      <div className="absolute inset-0 pointer-events-none opacity-50">
        <div
          className="absolute top-0 left-0 w-[700px] h-[700px] rounded-full blur-3xl"
          style={{ background: `radial-gradient(circle, ${OCSAS_GOLD}25 0%, transparent 70%)` }}
        />
        <div
          className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(31,58,42,0.6) 0%, transparent 70%)' }}
        />
      </div>

      <div className="container-swg relative z-10">
        {/* Eyebrow + heading block */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 max-w-3xl"
        >
          <span
            className="inline-block px-4 py-1.5 rounded-full text-[11px] tracking-[0.25em] uppercase mb-6 font-mono"
            style={{
              color: OCSAS_GOLD,
              background: `${OCSAS_GOLD}12`,
              border: `1px solid ${OCSAS_GOLD}44`,
            }}
            data-testid="ocsas-eyebrow"
          >
            {pick(ocsasPlatform.eyebrow)}
          </span>

          {/* Logo */}
          <div className="mb-6">
            <img
              src={ocsasPlatform.logo}
              alt="Ocsas Pharma"
              className="h-12 md:h-14 w-auto"
              data-testid="ocsas-logo"
            />
          </div>

          <h2 className="text-white text-3xl md:text-5xl font-light leading-[1.1] mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            {pick(ocsasPlatform.tagline)}
          </h2>

          <p className="text-[#d8d4c4] text-base md:text-lg leading-relaxed max-w-2xl">
            {pick(ocsasPlatform.shortBrief)}
          </p>
        </motion.div>

        {/* Chips + altitude landscape */}
        <div className="grid lg:grid-cols-[1fr,auto] gap-10 items-end mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-wrap gap-3"
          >
            {ocsasPlatform.chips.map((chip, i) => {
              const Icon = chipIcons[i] || Leaf;
              return (
                <div
                  key={i}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium"
                  style={{
                    background: 'rgba(15,31,23,0.6)',
                    border: `1px solid ${OCSAS_GOLD}33`,
                    color: '#f4ede0',
                  }}
                >
                  <Icon className="w-4 h-4" style={{ color: OCSAS_GOLD }} />
                  {pick(chip)}
                </div>
              );
            })}
          </motion.div>

          {/* Altitude marker */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-right"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: OCSAS_GOLD }}>
              {language === 'fr' ? ocsasPlatform.altitude.labelFr : ocsasPlatform.altitude.labelEn}
            </div>
            <div className="text-5xl md:text-6xl font-light tracking-tight" style={{ color: OCSAS_GOLD, fontFamily: 'Georgia, serif' }}>
              {ocsasPlatform.altitude.value}
            </div>
          </motion.div>
        </div>

        {/* Preview image with CTA overlay */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative rounded-2xl overflow-hidden border shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
          style={{ borderColor: `${OCSAS_GOLD}40` }}
        >
          <a
            href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
            className="block group"
            data-testid="ocsas-preview-link"
          >
            <img
              src={ocsasPlatform.preview}
              alt="Ocsas Pharma preview"
              className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.02]"
              loading="lazy"
            />
            {/* Bottom gradient + CTA */}
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
              <div className="flex items-center justify-end">
                <div
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm md:text-base shadow-lg transition-all group-hover:translate-x-1"
                  style={{
                    background: OCSAS_GOLD,
                    color: OCSAS_GREEN_DEEP,
                  }}
                >
                  {pick(ocsasPlatform.cta)}
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </a>
        </motion.div>

        {/* Footer line */}
        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs tracking-[0.2em] uppercase" style={{ color: `${OCSAS_GOLD}aa` }}>
          <span>Ocsas Pharma Ltd · Kinigi, Rwanda</span>
          <a
            href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
            className="inline-flex items-center gap-2 hover:text-white transition-colors"
            data-testid="ocsas-external-link"
          >
            EU-GMP Cultivation &amp; Oil Extraction
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default OcsasSection;
